/** Interface size, for a teacher who cannot read the default (2026-09-09).
 *
 * Three buttons on the control scale, so the group lines up with the language select beside
 * it: smaller, the current size (click to reset), larger. The keyboard shortcuts
 * (Cmd/Ctrl + - 0) are installed here, because the teacher chrome is on every teacher screen
 * and only teachers get this control -- a student's own phone already has the system's text
 * size, and the projector is sized for the room. Below `md` the header has no room for it and
 * the shortcuts still work. */
import { useEffect, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";

import {
  ZOOM_MAX,
  ZOOM_MIN,
  applyStoredZoom,
  installZoomShortcuts,
  resetZoom,
  stepZoom,
  subscribeZoom,
  zoom,
} from "../platform/zoom";

export function ZoomControl({ className = "" }: { className?: string }) {
  const { t, i18n } = useTranslation();
  const level = useSyncExternalStore(subscribeZoom, zoom, () => 1);

  useEffect(() => {
    applyStoredZoom();
    return installZoomShortcuts();
  }, []);

  const value = new Intl.NumberFormat(i18n.language, { style: "percent", maximumFractionDigits: 0 }).format(level);
  const btn =
    "inline-flex min-h-[var(--control-h)] min-w-[var(--control-h)] items-center justify-center px-2 hover:bg-[color:var(--surface-2)] disabled:opacity-40 disabled:hover:bg-transparent";
  return (
    <div
      role="group"
      aria-label={t("zoom.label")}
      className={[
        "hidden items-center rounded-[var(--radius-control)] border border-[color:var(--line)] bg-[color:var(--surface)] md:inline-flex",
        className,
      ].join(" ")}
    >
      <button
        type="button"
        className={`${btn} rounded-l-[var(--radius-control)] text-lg`}
        aria-label={t("zoom.out")}
        title={t("zoom.out")}
        disabled={level <= ZOOM_MIN}
        onClick={() => stepZoom(-1)}
      >
        <span aria-hidden>&#8722;</span>
      </button>
      <button
        type="button"
        className={`${btn} num border-x border-[color:var(--line)] text-sm tabular-nums`}
        aria-live="polite"
        aria-label={t("zoom.reset", { value })}
        title={t("zoom.reset", { value })}
        onClick={() => resetZoom()}
      >
        {value}
      </button>
      <button
        type="button"
        className={`${btn} rounded-r-[var(--radius-control)] text-lg`}
        aria-label={t("zoom.in")}
        title={t("zoom.in")}
        disabled={level >= ZOOM_MAX}
        onClick={() => stepZoom(1)}
      >
        <span aria-hidden>+</span>
      </button>
    </div>
  );
}
